import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { courses as seedCourses, units } from "../data/courses";
import type { Course, Level, Plan } from "../types/learning";
import type { LearningProfile } from "../types/onboarding";
interface AddCourseOptions {
  unit?: number;
  level?: Level;
}
interface LearnerUser {
  name: string;
  email: string;
  joined: string;
  initials: string;
}
interface LearnerContextValue {
  user: LearnerUser;
  plan: Plan;
  setPlan: (p: Plan) => void;
  courses: Course[];
  activeCourse: Course | undefined;
  setActiveCourseId: (id: string) => void;
  /** Adds a pair (or, with options, re-places an existing one at a new starting point). */
  addCourse: (target: string, base: string, opts?: AddCourseOptions) => string;
  learningProfile: LearningProfile | undefined;
  setLearningProfile: (p: LearningProfile) => void;
  completeLesson: (courseId: string, xp: number, advance?: boolean) => void;
  setBookPage: (courseId: string, page: number) => void;
  xpToday: number;
  dailyGoal: number;
  setDailyGoal: (n: number) => void;
  streak: number;
  totalXp: number;
}
const LearnerContext = createContext<LearnerContextValue | null>(null);
const user: LearnerUser = {
  name: "Maya Okafor",
  email: "maya@example.com",
  joined: "March 2026",
  initials: "MO"
};
interface LearnerProviderProps {
  plan: Plan;
  newLearner: boolean;
  children: ReactNode;
}
export function LearnerProvider({
  plan: initialPlan,
  newLearner,
  children
}: LearnerProviderProps) {
  const [plan, setPlan] = useState<Plan>(initialPlan);
  const [courses, setCourses] = useState<Course[]>(newLearner ? [] : seedCourses);
  const [activeId, setActiveId] = useState<string | undefined>(newLearner ? undefined : seedCourses[0]?.id);
  const [xpToday, setXpToday] = useState(newLearner ? 0 : 35);
  const [dailyGoal, setDailyGoal] = useState(50);
  const [streak, setStreak] = useState(newLearner ? 0 : 12);
  const [totalXp, setTotalXp] = useState(newLearner ? 0 : 8420);
  useEffect(() => {
    setPlan(initialPlan);
  }, [initialPlan]);
  useEffect(() => {
    setCourses(newLearner ? [] : seedCourses);
    setActiveId(newLearner ? undefined : seedCourses[0]?.id);
    setXpToday(newLearner ? 0 : 35);
    setStreak(newLearner ? 0 : 12);
    setTotalXp(newLearner ? 0 : 8420);
  }, [newLearner]);
  const [learningProfile, setLearningProfile] = useState<LearningProfile | undefined>();
  const addCourse = useCallback((target: string, base: string, opts?: AddCourseOptions) => {
    const id = `${target}-${base}`;
    setCourses(prev => {
      if (prev.some(c => c.id === id)) {
        if (!opts) return prev;
        return prev.map(c => c.id === id ? {
          ...c,
          unit: opts.unit ?? c.unit,
          lesson: 1,
          level: opts.level ?? c.level
        } : c);
      }
      const fresh: Course = {
        id,
        target,
        base,
        level: opts?.level ?? "A1",
        progress: 0,
        unit: opts?.unit ?? 1,
        lesson: 1,
        wordsLearned: 0,
        lastStudied: "Just now",
        weekXp: [0, 0, 0, 0, 0, 0, 0],
        skills: {
          vocab: 0,
          listening: 0,
          speaking: 0,
          grammar: 0,
          reading: 0
        },
        bookPage: 0
      };
      return [fresh, ...prev];
    });
    setActiveId(id);
    return id;
  }, []);
  const completeLesson = useCallback((courseId: string, xp: number, advance = true) => {
    setXpToday(v => v + xp);
    setTotalXp(v => v + xp);
    setStreak(s => s === 0 ? 1 : s);
    setCourses(prev => prev.map(c => {
      if (c.id !== courseId) return c;
      if (!advance) {
        const weekXp = [...c.weekXp];
        weekXp[6] += xp;
        return {
          ...c,
          weekXp,
          lastStudied: "Just now"
        };
      }
      const lessonsInUnit = units[c.unit - 1]?.lessons.length ?? 5;
      const nextLessonIdx = c.lesson + 1;
      const overflow = nextLessonIdx > lessonsInUnit;
      const weekXp = [...c.weekXp];
      weekXp[6] += xp;
      return {
        ...c,
        lesson: overflow ? 1 : nextLessonIdx,
        unit: overflow ? Math.min(c.unit + 1, units.length) : c.unit,
        progress: Math.min(c.progress + 4, 100),
        wordsLearned: c.wordsLearned + 8,
        lastStudied: "Just now",
        weekXp
      };
    }));
    setActiveId(courseId);
  }, []);
  const setBookPage = useCallback((courseId: string, page: number) => {
    setCourses(prev => prev.map(c => c.id === courseId && c.bookPage !== page ? {
      ...c,
      bookPage: page
    } : c));
  }, []);
  const value = useMemo<LearnerContextValue>(() => ({
    setBookPage,
    user,
    plan,
    setPlan,
    courses,
    activeCourse: courses.find(c => c.id === activeId) ?? courses[0],
    setActiveCourseId: setActiveId,
    addCourse,
    learningProfile,
    setLearningProfile,
    completeLesson,
    xpToday,
    dailyGoal,
    setDailyGoal,
    streak,
    totalXp
  }), [plan, courses, activeId, addCourse, learningProfile, completeLesson, setBookPage, xpToday, dailyGoal, streak, totalXp]);
  return <LearnerContext.Provider value={value} data-id="element-1247" data-mp="w5t59ujfd1j5">{children}</LearnerContext.Provider>;
}
export function useLearner(): LearnerContextValue {
  const ctx = useContext(LearnerContext);
  if (!ctx) throw new Error("useLearner must be used within LearnerProvider");
  return ctx;
}